import { createForm } from "@tanstack/solid-form";
import { produce } from "solid-js/store";
import type { z } from "zod";
import { useI18n } from "@/api/i18n";
import { FileInput } from "@/components/shared/input";
import { Button } from "@/components/ui/button";
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
} from "@/components/ui/input";
import { UPLOAD_RULES } from "@/domains/documents/constants/upload";
import {
  linkedinOnboardingValidationSchema,
  useOnboardingStore,
} from "../../context/OnboardingContext";

// Extract resume validation schema from the main schema
const curriculumValidationSchema =
  linkedinOnboardingValidationSchema.shape.info.pick({
    resume: true,
  });

type CurriculumFormValues = z.infer<typeof curriculumValidationSchema>;

interface CurriculumModalProps {
  onClose: () => void;
}

export const CurriculumModal = (props: CurriculumModalProps) => {
  const [store, setStore] = useOnboardingStore();
  const { t } = useI18n();

  const form = createForm(() => ({
    defaultValues: {
      resume: store.info.value.resume ?? undefined,
    } as CurriculumFormValues,
    onSubmit: async ({ value }) => {
      const validatedData = curriculumValidationSchema.safeParse(value);
      if (!validatedData.success) {
        console.error("Validation error", validatedData.error);
        // Update store with errors
        setStore(
          produce((draft) => {
            const errors = validatedData.error.flatten().fieldErrors;
            draft.info.error = errors.resume?.[0] ?? null;
          }),
        );
        return;
      }

      // Update store with validated data
      setStore(
        produce((draft) => {
          draft.info.value.resume = validatedData.data.resume;
          draft.info.error = null;
        }),
      );

      props.onClose();
    },
  }));

  const handleFileChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      form.setFieldValue("resume", file);
    } else {
      form.setFieldValue("resume", undefined as unknown as File);
    }
  };

  const handleRemoveResume = () => {
    form.setFieldValue("resume", undefined as unknown as File);
  };

  return (
    <div class="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>
          {t("profile.tabs.professional.edit.skills.resume.title")}
        </DialogTitle>
        <DialogDescription>
          Carica il tuo curriculum vitae in formato PDF
        </DialogDescription>
      </DialogHeader>

      <div class="w-full space-y-4">
        <form.Field
          name="resume"
          validators={{
            onChange: curriculumValidationSchema.shape.resume,
          }}
        >
          {(field) => (
            <FieldContainer>
              <FieldContainerLabel for="resume">
                {t("profile.tabs.professional.edit.skills.resume.title")}
              </FieldContainerLabel>
              <FileInput
                fieldApi={field()}
                onFileChange={handleFileChange}
                onRemoveFile={handleRemoveResume}
                uploadRules={UPLOAD_RULES.resume}
              />
              <FieldInfo field={field()} />
            </FieldContainer>
          )}
        </form.Field>
      </div>

      <div class="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={props.onClose}>
          {t("common.cancel")}
        </Button>
        <form.Subscribe
          selector={(state) => ({
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
            isValid: state.isValid,
            isSubmittable: curriculumValidationSchema.safeParse(state.values)
              .success,
          })}
        >
          {(state) => (
            <Button
              onClick={form.handleSubmit}
              disabled={
                !state().isValid ||
                state().isSubmitting ||
                !state().isSubmittable
              }
            >
              {state().isSubmitting ? t("common.saving") : t("common.save")}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </div>
  );
};
